import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { useState, useEffect, useRef } from "react";

const CTASection = () => {
  const { ref: sectionRef, isVisible } = useScrollAnimation({ threshold: 0.2 });
  const containerRef = useRef<HTMLDivElement>(null);
  const [parallaxOffset, setParallaxOffset] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      // Calculate how far into view the section is
      const progress = (windowHeight - rect.top) / (windowHeight + rect.height);
      setParallaxOffset(Math.max(0, Math.min(1, progress)));
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  
  return (
    <section 
      ref={(el) => {
        (sectionRef as React.MutableRefObject<HTMLElement | null>).current = el;
        (containerRef as React.MutableRefObject<HTMLElement | null>).current = el;
      }}
      className="py-12 md:py-24 px-4"
    >
      <div className="container mx-auto">
        {/* Main CTA Card */}
        <div className={`relative overflow-hidden rounded-2xl md:rounded-[2.5rem] bg-primary p-6 md:p-12 lg:p-16 min-h-[280px] md:min-h-[380px] transition-all duration-1000 ease-out motion-reduce:transition-none ${
          isVisible ? "opacity-100 scale-100 blur-0" : "opacity-0 scale-95 blur-sm"
        }`}>
          
          {/* Concentric circles decoration on right - with parallax */}
          <div 
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/4 hidden md:block transition-transform duration-100 ease-out motion-reduce:transition-none"
            style={{
              transform: `translateX(25%) translateY(calc(-50% + ${parallaxOffset * 40 - 20}px))`,
            }}
          >
            <div 
              className="relative w-[500px] h-[500px] lg:w-[600px] lg:h-[600px] transition-transform duration-100 ease-out motion-reduce:transition-none"
              style={{
                transform: `scale(${0.95 + parallaxOffset * 0.1}) rotate(${parallaxOffset * 15}deg)`,
              }}
            >
              {/* Outer circle */}
              <div className="absolute inset-0 rounded-full bg-primary-foreground/10" />
              {/* Middle circle */}
              <div className="absolute inset-[15%] rounded-full bg-primary-foreground/15" />
              {/* Inner circle */}
              <div className="absolute inset-[30%] rounded-full bg-primary-foreground/20" />
              {/* Center circle */}
              <div className="absolute inset-[45%] rounded-full bg-primary-foreground/30" />
              {/* Core */}
              <div className="absolute inset-[60%] rounded-full bg-primary-foreground/50" />
            </div>
          </div>

          {/* Mobile circles - smaller version with subtle parallax */}
          <div 
            className="absolute right-0 bottom-0 translate-x-1/3 translate-y-1/3 md:hidden transition-transform duration-100 ease-out motion-reduce:transition-none"
            style={{
              transform: `translateX(33%) translateY(calc(33% - ${parallaxOffset * 20}px)) rotate(${parallaxOffset * 10}deg)`,
            }}
          >
            <div className="relative w-[250px] h-[250px]">
              <div className="absolute inset-0 rounded-full bg-primary-foreground/10" />
              <div className="absolute inset-[20%] rounded-full bg-primary-foreground/15" />
              <div className="absolute inset-[40%] rounded-full bg-primary-foreground/25" />
              <div className="absolute inset-[60%] rounded-full bg-primary-foreground/40" />
            </div>
          </div>

          {/* Content */}
          <div className="relative z-10 max-w-xl">
            {/* Main heading */}
            <h2 
              className={`text-2xl md:text-4xl lg:text-5xl font-bold text-primary-foreground mb-3 md:mb-6 leading-tight transition-all duration-500 motion-reduce:transition-none ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
              }`}
            >
              Let's Build the Future of DePIN.
            </h2>
            
            {/* Description */}
            <p 
              className={`text-sm md:text-lg text-primary-foreground/80 mb-6 md:mb-10 max-w-md leading-relaxed transition-all duration-500 delay-100 motion-reduce:transition-none ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
              }`}
            >
              Join Africa's premier DePIN community and be part of the movement transforming physical infrastructure.
            </p>
            
            {/* CTA Buttons */}
            <div 
              className={`flex flex-col sm:flex-row gap-2 sm:gap-4 transition-all duration-500 delay-150 motion-reduce:transition-none ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
              }`}
            >
              <Button 
                asChild 
                size="lg" 
                className="group bg-background hover:bg-background/90 text-foreground font-semibold rounded-full px-5 h-11 sm:h-12 w-full sm:w-auto"
              >
                <a href="https://discord.gg/paEy3nMcuH" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-3">
                  <span>Join Discord</span>
                  <span className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-foreground/20 flex items-center justify-center">
                    <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-foreground group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </a>
              </Button>
              
              <Button 
                asChild 
                size="lg" 
                className="group bg-background hover:bg-background/90 text-foreground font-semibold rounded-full px-5 h-11 sm:h-12 w-full sm:w-auto"
              >
                <Link to="/ecosystem" className="flex items-center justify-center gap-3">
                  <span>Explore Ecosystem</span>
                  <span className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-foreground/20 flex items-center justify-center">
                    <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-foreground group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </Link>
              </Button>
            </div>
          </div>
          
        </div>
      </div>
    </section>
  );
};

export default CTASection;
